// Style
import styled from 'styled-components'

// Core
import React, {Fragment, useEffect, useState} from 'react'

// Components, actions, models, etc.
import LinkButton from 'components/LinkButton'
import RadioGroup from 'components/RadioGroup'
import {CDA_HOMEPAGE_URL} from 'constants/appConstants'
import {CDAPriceType, useCDAProductPrice} from 'hooks/useProductPrice'
import {AddressType} from 'types/propertyTypes'
import AddonFormField from './AddonFormField'
import FormField from './FormField'
import {sortByObjectKeys} from 'services/sort'

// Third party
import moment from 'moment'
import {Field} from 'react-final-form'

const DeliveryOptionFormFields = ({field, address}: {field: string; address: AddressType}) => {
  const [isMLSEnabled, setIsMLSEnabled] = useState(false)
  const [options, setOptions] = useState<CDAPriceType[]>([])
  const {data} = useCDAProductPrice(address)

  useEffect(() => {
    if (data.length) {
      const options = data.filter(option => option.isMLSEnabled === isMLSEnabled)
      const sortedData = sortByObjectKeys(options, 'turnTime')
      setOptions(sortedData)
    }
  }, [isMLSEnabled, data])

  return (
    <Fragment>
      <AddonFormField checked={isMLSEnabled} onChange={e => setIsMLSEnabled(e.target.checked)} />
      <DeliveryOptionsFormField label='Delivery Options'>
        <p className='sub-title'>
          {`Please note the cost is reflective of report prioritization and estimated turn
                times. For more information, refer to our `}
          <LinkButton variant='tertiary' title='website' href={CDA_HOMEPAGE_URL} target='_blank' />
          {'.'}
        </p>
        <Field name={field}>
          {({input}) => {
            return (
              <DeliveryOptionGroup
                options={options}
                value={input.value}
                onChange={input.onChange}
              />
            )
          }}
        </Field>
      </DeliveryOptionsFormField>
    </Fragment>
  )
}

const getDeliveryOption = (options: CDAPriceType[], dueDate?: string) => {
  return (
    options.find(option => option.dueDate === dueDate) ||
    options.find(option => option.turnTime === 24) ||
    options[0]
  )
}

const DeliveryOptionGroup = ({
  value,
  onChange,
  options
}: {
  options: CDAPriceType[]
  value?: CDAPriceType
  onChange: (value: CDAPriceType) => void
}) => {
  const {dueDate} = value || {}

  useEffect(() => {
    if (options.length && !options.find(option => option === value)) {
      // we prefer 2 days as default, but incase backend do not return this one
      const option = getDeliveryOption(options, value?.dueDate)
      onChange(option)
    }
  }, [onChange, options, value])

  return (
    <RadioGroup
      value={dueDate || ''}
      onChange={(e, newDueDate) => onChange(getDeliveryOption(options, newDueDate))}
      options={options.map(({price, dueDate, turnTime}) => {
        return {
          value: dueDate,
          label: (
            <DueDateOption>
              <div className='title-container'>
                <p className='title'>{turnTime} Business Day</p>
                <p className='description'>Estimated {moment(dueDate).format('MMM D, YYYY')}</p>
              </div>
              <h5 className='price'>${price}</h5>
            </DueDateOption>
          )
        }
      })}
    />
  )
}

const DueDateOption = styled.div`
  display: flex;
  margin-left: 8px;

  .title-container {
    min-width: 160px;
    margin-right: 24px;
  }

  .title {
    margin: 0;
    line-height: 22px;
  }

  .description {
    margin: 0;
    color: ${({theme}) => theme.colors.grayscale.gray};
    line-height: 22px;
  }

  .price {
    margin-bottom: 0;
    margin-left: 16px;
    font-weight: 500;
    line-height: 22px;
    text-align: right;
    min-width: 46px;
  }
`
const DeliveryOptionsFormField = styled(FormField)`
  .form-field-header {
    margin-bottom: 8px;
  }

  .sub-title {
    margin-bottom: 16px;
    margin-top: 0px;
    color: ${({theme}) => theme.colors.grayscale.gray};
  }

  .link-button {
    min-width: unset;
  }

  .portal-MuiFormControlLabel-root {
    align-items: start;
  }

  .portal-MuiFormControl-root {
    padding-bottom: 0;
  }

  .portal-MuiFormGroup-root {
    row-gap: 16px;
  }
`

export default DeliveryOptionFormFields
